import MoreVertRoundedIcon from '@mui/icons-material/MoreVertRounded';
import Button, { type ButtonProps } from '@mui/material/Button';
import IconButton, { type IconButtonProps } from '@mui/material/IconButton';
import useControlled from '@mui/utils/useControlled';
import type { ReactElement, ReactNode } from 'react';
import React, { useCallback, useId, useState } from 'react';

import { QMenu } from './QMenu';
import type { QMenuProps } from '../types';

const DEFAULT_ICON = <MoreVertRoundedIcon />;

type QDropdownMenuSharedProps = {
  /** Menu contents — action rows auto-dismiss via QMenu close context. */
  children: ReactNode;
  /** Base id for button + menu pairing (`${id}-button` / `${id}`). */
  id?: string;
  disabled?: boolean;
  width?: QMenuProps['width'];
  dense?: QMenuProps['dense'];
  /** Passed to QMenu. Defaults to `"menu"` (action overflow). */
  variant?: QMenuProps['variant'];
  /** Controlled open state. Omit to let QDropdownMenu manage its own open/close. */
  open?: boolean;
  /** Fires on open (trigger click) and close, in both controlled and uncontrolled mode. */
  onOpenChange?: (open: boolean) => void;
};

export type QDropdownMenuIconProps = QDropdownMenuSharedProps & {
  /** IconButton trigger (default). Overflow / 3-dot menus. */
  triggerVariant?: 'icon';
  /** Accessible name for the IconButton. Defaults to "More actions". */
  'aria-label'?: string;
  /** Trigger icon. Defaults to MoreVert. */
  icon?: ReactNode;
  /**
   * IconButton visual variant. Defaults to `"tonal"` when omitted (overflow DS default).
   * Use `"plain"` for IconButton's natural default look (no `variant` prop passed through).
   * Use `"contained" | "outlined" | "tonal"` to pass that variant to IconButton.
   */
  iconButtonVariant?: IconButtonProps['variant'] | 'plain';
  /** IconButton shape. Defaults to `"rounded"`. */
  iconButtonShape?: IconButtonProps['shape'];
  /** IconButton size. */
  iconButtonSize?: IconButtonProps['size'];
  /**
   * Extra props for the trigger. Cannot override wiring owned by QDropdownMenu
   * (`id`, `onClick`, menu a11y attrs, `variant`, `shape`, `size`, `disabled`).
   */
  IconButtonProps?: Omit<
    IconButtonProps,
    | 'id'
    | 'onClick'
    | 'aria-controls'
    | 'aria-haspopup'
    | 'aria-expanded'
    | 'aria-label'
    | 'disabled'
    | 'variant'
    | 'shape'
    | 'size'
    | 'ref'
    | 'sharedcomponentid'
    | 'onKeyDown'
  > & {
    'data-testid'?: string;
  };
};

export type QDropdownMenuButtonProps = QDropdownMenuSharedProps & {
  /** Labeled Button trigger — whole control opens the menu. */
  triggerVariant: 'button';
  /** Visible label on the Button. */
  label: ReactNode;
  /** Button visual variant. Defaults to `"outlined"`. */
  buttonVariant?: ButtonProps['variant'];
  /** Button size. */
  buttonSize?: ButtonProps['size'];
  /** Button color. */
  buttonColor?: ButtonProps['color'];
  /**
   * Extra props for the trigger. Cannot override wiring owned by QDropdownMenu
   * (`id`, `onClick`, menu a11y attrs, `variant`, `size`, `color`, `disabled`, `children`).
   */
  ButtonProps?: Omit<
    ButtonProps,
    | 'id'
    | 'onClick'
    | 'aria-controls'
    | 'aria-haspopup'
    | 'aria-expanded'
    | 'disabled'
    | 'variant'
    | 'size'
    | 'color'
    | 'children'
    | 'ref'
    | 'sharedcomponentid'
    | 'onKeyDown'
  >;
};

export type QDropdownMenuProps = QDropdownMenuIconProps | QDropdownMenuButtonProps;

/**
 * Trigger + QMenu composition for action / overflow menus.
 * Owns open state and wires MUI menu-button a11y (`aria-haspopup`, `aria-controls`, `aria-expanded`).
 *
 * - `triggerVariant="icon"` (default) — IconButton (3-dot overflow)
 * - `triggerVariant="button"` — labeled Button; whole control opens the menu
 *
 * Use standalone {@link QMenu} when you need a fully custom trigger (ListItem, etc.).
 * For classic split buttons, use legacy `components/SplitButton`.
 */
export function QDropdownMenu(props: QDropdownMenuProps): ReactElement {
  const {
    children,
    id: idProp,
    disabled = false,
    width,
    dense,
    variant = 'menu',
    open: openProp,
    onOpenChange,
  } = props;

  const triggerVariant = props.triggerVariant ?? 'icon';

  const reactId = useId();
  const menuId = idProp ?? `q-menu${reactId}`;
  const buttonId = `${menuId}-button`;

  // Callback-ref → state so a parent-forced `open` re-renders with a real anchorEl
  // (plain useRef.current is still null on the first open=true render).
  const [anchorEl, setAnchorEl] = useState<HTMLElement | null>(null);
  const setAnchorRef = useCallback((node: HTMLElement | null) => {
    setAnchorEl((prev) => (prev === node ? prev : node));
  }, []);
  const [open, setOpen] = useControlled({
    controlled: openProp,
    default: false,
    name: 'QDropdownMenu',
  });

  const handleOpen = useCallback(() => {
    setOpen(true);
    onOpenChange?.(true);
  }, [setOpen, onOpenChange]);

  const handleClose = useCallback(() => {
    setOpen(false);
    onOpenChange?.(false);
  }, [setOpen, onOpenChange]);

  /** APG menu-button: ArrowDown/ArrowUp open and move focus into the menu. */
  const handleTriggerKeyDown = useCallback(
    (event: React.KeyboardEvent<HTMLElement>) => {
      if (disabled || open) return;
      if (event.key === 'ArrowDown' || event.key === 'ArrowUp') {
        event.preventDefault();
        handleOpen();
      }
    },
    [disabled, open, handleOpen],
  );

  const menu = (
    <QMenu
      id={menuId}
      anchorEl={anchorEl}
      open={open}
      onClose={handleClose}
      width={width}
      dense={dense}
      variant={variant}
    >
      {children}
    </QMenu>
  );

  if (triggerVariant === 'button') {
    const {
      label,
      buttonVariant = 'outlined',
      buttonSize,
      buttonColor,
      ButtonProps,
    } = props as QDropdownMenuButtonProps;

    return (
      <>
        <Button
          ref={setAnchorRef}
          id={buttonId}
          variant={buttonVariant}
          size={buttonSize}
          color={buttonColor}
          aria-controls={open ? menuId : undefined}
          aria-haspopup='true'
          aria-expanded={open}
          disabled={disabled}
          onClick={handleOpen}
          onKeyDown={handleTriggerKeyDown}
          sharedcomponentid='QDropdownMenu'
          {...ButtonProps}
        >
          {label}
        </Button>
        {menu}
      </>
    );
  }

  // triggerVariant === 'icon' (default)
  const {
    'aria-label': ariaLabel = 'More actions',
    icon = DEFAULT_ICON,
    iconButtonVariant = 'tonal',
    iconButtonShape = 'rounded',
    iconButtonSize,
    IconButtonProps,
  } = props as QDropdownMenuIconProps;

  // `"plain"` = IconButton default look (omit variant). Other values pass through.
  const iconVariantProp = iconButtonVariant === 'plain' ? undefined : { variant: iconButtonVariant };

  return (
    <>
      <IconButton
        {...iconVariantProp}
        shape={iconButtonShape}
        size={iconButtonSize}
        ref={setAnchorRef}
        id={buttonId}
        aria-label={ariaLabel}
        aria-controls={open ? menuId : undefined}
        aria-haspopup='true'
        aria-expanded={open}
        disabled={disabled}
        onClick={handleOpen}
        onKeyDown={handleTriggerKeyDown}
        sharedcomponentid='QDropdownMenu'
        {...IconButtonProps}
      >
        {icon}
      </IconButton>
      {menu}
    </>
  );
}
